/**
 * Guards the article-open seoScore flush.
 *
 * SeoScore, LucyPanel and LucyNudge each watch the same computed score. The
 * read calls getContentToExport, which used to replace article fields on every
 * pass. That notified the other watchers, which read the score again. Vue 2
 * production builds do not stop the flush. Writes during the read are ignored,
 * and a commit is kept only when the stored number actually changes.
 */

let reading = 0;
let writing = false;

export function isSeoScoreReadActive(): boolean {
    return reading > 0;
}

/** Run a score read. Store commits from inside it are ignored. */
export function readSeoScore<T>(read: () => T): T {
    reading += 1;
    try {
        return read();
    } finally {
        reading -= 1;
    }
}

/** True when `next` may be written onto article.seoScore. */
export function canAssignSeoScore(current: unknown, next: unknown): boolean {
    if (reading > 0) {
        return false;
    }
    return typeof next === 'number' && Number.isFinite(next) && !Object.is(current, next);
}

type SeoScoreCommitStore = {
    getters: {article?: {seoScore?: number} | null};
    commit: (type: string, value: number) => void;
};

/**
 * Commit the computed score once per flush, and only when it differs from both
 * the previous computed value and the number already stored on the article.
 * The lock stays up through the current synchronous flush (cleared on a
 * microtask, which runs after Vue's watcher queue).
 */
export function commitArticleSeoScore(
    store: SeoScoreCommitStore,
    next: number,
    previous: number
): boolean {
    if (writing || Object.is(next, previous)) {
        return false;
    }
    const stored = store.getters.article ? store.getters.article.seoScore : undefined;
    if (!canAssignSeoScore(stored, next)) {
        return false;
    }
    writing = true;
    try {
        store.commit('setArticleSeoScore', next);
        return true;
    } finally {
        Promise.resolve().then(() => {
            writing = false;
        });
    }
}
